import React, { useState, useEffect, useCallback } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Campaign, 
  GameCard, 
  CampaignDeck,
  Character,
  CharacterDrawnCard,
} from "@dndModels";
import { DisablableOption, ModelBase } from "@models";

import API from "@dndUtilities/system_api";

import ButtonBox from "@components/input/buttons/ButtonBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import StringBox from "@components/input/strings/StringBox";
import LoadingBox from "@components/input/LoadingBox";
import CheckBox from "@components/input/CheckBox";
import ObjectButtonBox from "@components/input/buttons/ObjectButtonBox";
import CommonUtilities from "@utilities/common_utilities";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import SelectObjectIndex from "@dndComponents/select/SelectObjectIndex";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import CardRoller from "@dndComponents/other/CardRoller";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CardsDrawnByCharactersInput from "@dndComponents/character/campaign_deck/CardsDrawnByCharactersInput";
import AdventureSpreadsInput from "@dndComponents/character/campaign_deck/AdventureSpreadsInput";

interface Props { 
  campaign: Campaign;
  disabled: boolean;
  onChange: (changed: Campaign) => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CampaignDecksInput({ 
  campaign, 
  disabled,
  onChange
}: Props) {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reload_time, setReloadTime] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [using_deck, setUsingDeck] = useState<CampaignDeck | null>(null);
  const [using_deck_visible, setUsingDeckVisible] = useState(false);
  const [show_current_order, setShowCurrentOrder] = useState(false);
  const [decks, setDecks] = useState<CampaignDeck[]>([]);
  const [cards, setCards] = useState<GameCard[]>([]);
  const [ping, setPing] = useState(false);
  const [show_cards, setShowCards] = useState(false);
  const [view_mode, setViewMode] = useState("Controls");
  const [show_drawn, setShowDrawn] = useState(false);
  const [card, setCard] = useState<GameCard | null>(null);
  const [filter, setFilter] = useState<any>({ });
  const [selected_character_id, setSelectedCharacterId] = useState("");
  const [characters, setCharacters] = useState<Character[]>([]);
  const [character_options, setCharacterOptions] = useState<DisablableOption[]>([]);
  const [character_drawn_cards, setCharacterDrawnCards] = useState<CharacterDrawnCard[]>([]);
  const [draw_details, setDrawDetails] = useState("");
  const [drawing_cards, setDrawingCards] = useState(false);
  
  const load = useCallback(async () => {
    setLoading(true);
    const new_chars = await api.getPlayerObjects({ data_type: "character", filter: { campaign_id: campaign._id }}) as Character[];
    setCharacters(new_chars);
    const new_character_options = new_chars.filter(o => o.active && !o.is_archived).map(o => {
      const option: DisablableOption = {
        id: o._id,
        text: o.name,
        disabled: false,
      };
      return option;
    });
    setCharacterOptions(new_character_options);

    const drawn_cards: CharacterDrawnCard[] = [];
    for (const char of new_chars) {
      const char_drawn_cards = await api.getPlayerObjects({ data_type: "character_drawn_card", filter: { character_id: char._id }});
      drawn_cards.push(...char_drawn_cards as CharacterDrawnCard[]);
    }
    setCharacterDrawnCards(drawn_cards);

    const cards = await api.getObjects({ data_type: "game_card", character_options: campaign.character_options });
    setCards(cards as GameCard[]);
    const decks = await api.getObjects({ data_type: "campaign_deck", filter: { campaign_id: campaign._id }});
    setDecks(decks as CampaignDeck[]);
    setLoading(false);
  }, [campaign, setLoading]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [load, fetched, setFetched]);

  useEffect(() => {
    if (reloading) {
      setReloadTime(common_util.get_time_number());
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const addNew = () => {
    setUsingDeck(new CampaignDeck());
    setUsingDeckVisible(false);
  };

  const updateDeck = () => {
    if (using_deck) {
      setProcessing(true);
      if (using_deck._id === "") {
        using_deck.campaign_id = campaign._id;
        api.upsertObject("campaign_deck", using_deck).then(res => {
          if (res) {
            const new_using_deck = using_deck;
            new_using_deck._id = res.body.id;
            // campaign.decks_of_many_things.push({ id: new_using_deck._id, visible: using_deck_visible });

            const new_decks = decks;
            new_decks.push(new_using_deck);
            setDecks(new_decks);

            onChange(campaign);
            // setUsingDeck(null);
          }
          setProcessing(false);
        });
      } else {
        api.upsertObject("campaign_deck", using_deck).then(res => {
          // onChange(campaign);
          // const d = campaign.decks_of_many_things.find(o => o.id === using_deck._id);
          // if (d) {
          //   d.visible = using_deck_visible;
          // }
          // onChange(campaign);
          // setUsingDeck(null);
          setProcessing(false);
        });
      }
    }
  };

  const refreshDeck = () => {
    if (using_deck) {
      api.getObjects({ data_type: "campaign_deck", filter: { "_id*ObjectIDs": [using_deck._id] } }).then(res => {
        if (res.length === 1 && res[0] instanceof CampaignDeck) {
          using_deck.copy(res[0]);
          setPing(!ping);
        }
      });
    }
  }

  const drawCard = () => {
    // debugger;
    if (using_deck) {
      api.getObjects({ data_type: "campaign_deck", filter: { "_id*ObjectIDs": [using_deck._id] } }).then(res => {
        if (res.length === 1 && res[0] instanceof CampaignDeck) {
          const deck = res[0];
          drawCardFromDeck(deck);
        } else {
          console.error(res);
        }
        setLoading(false);
      });
    }
  }

  const drawCardFromDeck = (deck: CampaignDeck) => {
    const char = characters.find(o => o._id === selected_character_id);
    if (char) {
      const drawn_cards = character_drawn_cards.filter(o => o.char_id === char._id);
      let look_at_ids = [...deck.fudged_draw_ids, ...deck.card_ids];
      let c_id = look_at_ids[0];
      let drawn_card = drawn_cards.find(o => o.card_id === c_id);
      while (drawn_card) {
        const using_c_id = c_id;
        look_at_ids = look_at_ids.filter(o => o !== using_c_id);
        c_id = look_at_ids[0];
        drawn_card = drawn_cards.find(o => o.card_id === using_c_id);
      }
      if (c_id === "") {
        // setDeckEmpty(true);
        console.log("Empty");
      } else {
        api.getFullObject("game_card", c_id).then(res => {
          if (res instanceof GameCard) {
            const card = res;
            if (deck.fudged_draw_ids.length === 0 && deck.fudged_for_beneficial && !card.beneficial) {
              deck.card_ids = deck.card_ids.filter(o => o !== card._id);
              deck.fudged_draw_ids = deck.fudged_draw_ids.filter(o => o !== card._id);
              if (card.reshuffle) {
                deck.drawn_card_ids.push(card._id);
              }
              api.updateObject("campaign_deck", deck).then(res2 => {
                drawCard();
              });
            } else {
              setCard(res);
              deck.card_ids = deck.card_ids.filter(o => o !== card._id);
              deck.fudged_draw_ids = deck.fudged_draw_ids.filter(o => o !== card._id);
              if (card.reshuffle) {
                deck.drawn_card_ids.push(card._id);
              }
              api.updateObject("campaign_deck", deck).then(res2 => {
                setProcessing(false);
                deck.copy(deck);
                setPing(!ping);
                if (deck.card_ids.length === 0) {
                  console.log("Empty");
                }
              });
            }
          } else if (res === null) {
            // It's a card that's been deleted.  Remove it from the deck, and then get the next one.
            deck.card_ids = deck.card_ids.filter(o => o !== c_id);
            deck.drawn_card_ids = deck.drawn_card_ids.filter(o => o !== c_id);
            api.updateObject("campaign_deck", deck).then(res2 => {
              drawCard();
            });
          } else {
            console.error(res);
            setProcessing(false);
          }
        });
      }
    }
  }

  const renderSaveDrawnCard = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Draw Details" 
            value={draw_details}
            onChange={setDrawDetails}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <ButtonBox  
            name="Save Drawn Card"
            disabled={disabled || processing || card === null || selected_character_id === ""}
            onClick={ async () => {
              if (card && selected_character_id !== "") {
                const drawn_card = new CharacterDrawnCard();
                drawn_card.char_id = selected_character_id;
                drawn_card.drawn_dt = common_util.get_time_number();  
                drawn_card.card_id = card._id;
                drawn_card.details = draw_details;
                setProcessing(true);
                await api.upsertObject("character_drawn_card", drawn_card);
                setProcessing(false);
              }
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={ drawing_cards }
        onClose={() => {
          setDrawingCards(false);
          setCard(null);
          setSelectedCharacterId("");
        }}
      >
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name="Character"
              value={selected_character_id}
              options={character_options}
              onChange={(changed: string) => {
                setSelectedCharacterId(changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <ButtonBox
              name={ card ? "Draw Another Card" : "Draw a Card" }
              disabled={disabled || processing || selected_character_id === ""}
              onClick={() => {
                setProcessing(true);
                drawCard();
              }}
            />
          </Grid>
          { card &&
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                { renderRoll() }
              </Grid>
              <Grid
                size={{
                  xs: 12
                }}>
                { renderSaveDrawnCard() }
              </Grid>
            </>
          }
        </Grid>
      </ModalOrDrawer>
    );
  }

  const renderRoll = () => {
    if (card) {
      // const selected_character = characters.find(o => o._id === selected_character_id);
      return (
        <CardRoller 
          name="Card"
          // char={selected_character}
          card={card}
          width={522}
          onApply={() => {
            // onChange();
          }}
        />
      );
    }
    return null;
  }

  const removeDeck = (deck: CampaignDeck) => {
    setProcessing(true);
    api.deleteObject("campaign_deck", deck).then(res => {
      // campaign.decks_of_many_things = campaign.decks_of_many_things.filter(o => o.id !== deck._id);

      const new_decks = decks.filter(o => o._id !== deck._id)
      setDecks(new_decks);

      onChange(campaign);
      setUsingDeck(null);
      setProcessing(false);
    });
  };

  const renderDecks = () => {
    if (!using_deck) {
      return (
        <>
          <Grid
            size={{
              xs: 2
            }}>
            <AddButton
              disabled={disabled || processing}
              onClick={addNew}
            />
          </Grid>
          <Grid
            className="header centered"
            size={{
              xs: 8
            }}>
            Campaign Decks for { campaign.name }
          </Grid>
          <Grid
            size={{
              xs: 2
            }}>
          </Grid>
          { decks.map((deck, key) => {
            const class_name = key % 2 === 1 ? "alternate" : "";
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                className={class_name}
                size={{
                  xs: 12
                }}>
                <Grid
                  onClick={() => {
                    // const d = campaign.decks_of_many_things.find(o => o.id === deck._id);
                    // if (d) {
                    setUsingDeck(deck);
                    //   setUsingDeckVisible(d.visible);
                    // }
                  }}
                  style={{ cursor: "pointer" }}
                  size={{
                    xs: 9
                  }}>
                  { deck.name }
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <DeleteButton 
                    disabled={disabled || processing}
                    onClick={() => {
                      removeDeck(deck);
                    }}
                  />
                </Grid>
              </Grid>
            );
          })}
        </>
      );
    }
    return null;
  };

  const shuffleDeck = (deck: CampaignDeck) => {
    deck.card_ids = common_util.shuffleList(deck.card_ids) as string[];
  }

  const renderCards = () => {
    if (using_deck) {
      // debugger;
      let the_cards = [...using_deck.card_ids];
      if (using_deck.fudged_for_beneficial) {
        the_cards = the_cards.filter(o => {
          const card = cards.find(o2 => o2._id === o);
          return card && card.beneficial;
        });
      }
      if (!show_current_order) {
        the_cards = the_cards.sort((a,b) => {
          const cardA = cards.find(o => o._id === a);
          const cardB = cards.find(o => o._id === b);
          if (cardA && cardB) {
            return cardA.name.localeCompare(cardB.name);
          }
          return 1;
        });
      }
      return (
        <Grid container spacing={0} direction="row">
          { !reloading && the_cards.map((c_id, key) => {
            return (
              <Grid
                key={key}
                style={{ border: (using_deck.fudged_draw_ids.includes(c_id) ? "1px solid blue" : "inherit")}}
                onClick={() => {
                  if (using_deck.fudged_draw_ids.includes(c_id)) {
                    using_deck.fudged_draw_ids = using_deck.fudged_draw_ids.filter(o => o !== c_id);
                  } else {
                    using_deck.fudged_draw_ids.push(c_id);
                  }
                  setPing(!ping);
                }}
                size={{
                  xs: 1
                }}>
                <DisplayObjects
                  ids={[c_id]}
                  type="game_card"
                  display_image
                />
              </Grid>
            );
          })}
        </Grid>
      );
    }
    return null;
  }

  const renderControls = () => {
    if (using_deck) {
      return (
        <>
          <Grid
            size={{
              xs: 3
            }}>
            <CheckBox 
              name="Show Cards in Deck"
              value={show_cards} 
              disabled={disabled || processing}
              onChange={(changed: boolean) => {
                setShowCards(changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            { show_cards &&
              <CheckBox 
                name="Show Current Order"
                value={show_current_order} 
                disabled={disabled || processing}
                onChange={(changed: boolean) => {
                  setShowCurrentOrder(changed);
                  setReloading(true);
                }}
              />
            }
          </Grid>
          <Grid
            size={{
              xs: 2
            }}>
            { using_deck.drawn_card_ids.length > 0 &&
              <CheckBox 
                name="Show Drawn Cards"
                value={show_drawn} 
                disabled={disabled || processing}
                onChange={(changed: boolean) => {
                  setShowDrawn(changed);
                  setReloading(true);
                }}
              />
            }
          </Grid>
          <Grid
            size={{
              xs: 2
            }}>
            <CheckBox
              name="Beneficial Only"
              value={using_deck.fudged_for_beneficial}
              onChange={() => { 
                using_deck.fudged_for_beneficial = !using_deck.fudged_for_beneficial;
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 2
            }}>
            <CheckBox 
              name="Visible to Players"
              value={using_deck_visible} 
              disabled={disabled || processing}
              onChange={setUsingDeckVisible}
            />
          </Grid>
          { show_cards && 
            <>
              <Grid
                className='header centered'
                size={{
                  xs: 12
                }}>Cards in Deck</Grid>
              <Grid
                size={{
                  xs: 12
                }}>
                { renderCards() }
              </Grid>
            </>
          }
          { show_drawn && using_deck.drawn_card_ids.length > 0 &&
            <>
              <Grid
                className='header centered'
                size={{
                  xs: 12
                }}>Drawn Cards</Grid>
              <Grid
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                { !reloading && using_deck.drawn_card_ids.map((id, key) => {
                  const card = cards.find(o => o._id === id);
                  if (card) {
                    return (
                      <Grid
                        key={key}
                        size={{
                          xs: 2
                        }}>
                        <ObjectButtonBox
                          obj={card}
                          display_format="Shuffle {obj.name} Back In"
                          show_image
                          string_limit={100}
                          onClick={() => {
                            const new_using_deck = using_deck;
                            new_using_deck.card_ids.push(id);
                            new_using_deck.drawn_card_ids = new_using_deck.drawn_card_ids.filter(o => o !== id);
                            shuffleDeck(new_using_deck);
                            setUsingDeck(new_using_deck);
                            setReloading(true);
                          }}
                        />
                      </Grid>
                    );
                  }
                  return null;
                })}
              </Grid>
            </>
          }
          <Grid
            size={{
              xs: 4
            }}>
            <ButtonBox
              name="Shuffle All"
              disabled={disabled || processing}
              onClick={() => {
                const new_using_deck = using_deck;
                new_using_deck.card_ids.push(...new_using_deck.drawn_card_ids);
                new_using_deck.drawn_card_ids = [];
                shuffleDeck(new_using_deck);
                setUsingDeck(new_using_deck);
                // if (show_current_order) {
                  setReloading(true);
                // }
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <ButtonBox
              name="Draw Cards"
              disabled={using_deck._id === "" || disabled || processing}
              onClick={() => {
                setDrawingCards(true);
              }}
            />
            { renderExtras() }
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <ButtonBox
              name="Refresh from DB"
              disabled={using_deck._id === "" || disabled || processing}
              onClick={refreshDeck}
            />
          </Grid>
        </>
      );
    }
    return null;
  }

  const renderSelectCardsInDeck = () => {
    // debugger;
    if (using_deck) {
      let the_cards = [...using_deck.card_ids];
      the_cards = the_cards.sort((a,b) => {
        const cardA = cards.find(o => o._id === a);
        const cardB = cards.find(o => o._id === b);
        if (cardA && cardB) {
          return cardA.name.localeCompare(cardB.name);
        }
        return 1;
      });
      const use_filter = {...filter};
      use_filter.deck_ids = using_deck.deck_id;
      return (
        <>
          <Grid
            size={{
              xs: 6
            }}>
            <ButtonBox
              name="Select All"
              onClick={() => {
                const new_using_deck = using_deck;
                new_using_deck.card_ids = cards.filter(o => o.deck_ids.includes(using_deck.deck_id)).map(o => o._id);
                shuffleDeck(new_using_deck);
                setUsingDeck(new_using_deck);
                setReloading(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <ButtonBox
              name="Unselect All"
              onClick={() => {
                const new_using_deck = using_deck;
                new_using_deck.card_ids = [];
                shuffleDeck(new_using_deck);
                setUsingDeck(new_using_deck);
                setReloading(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            { using_deck.deck_id !== "" &&
              <SelectObjectIndex
                data_type='game_card'
                values={the_cards}
                reload_time={reload_time}
                filter={use_filter}
                updateFilter={setFilter}
                show_image
                // log_it
                disableObj={(obj: ModelBase) => {
                  if (using_deck.drawn_card_ids.includes(obj._id)) {
                    return { disabled: true, reasons: "In deck, but has been drawn" };
                  }
                  return { disabled: false, reasons: "" };
                }}
                onChange={(changed: string[]) => {
                  const new_using_deck = using_deck;
                  new_using_deck.card_ids = changed;
                  shuffleDeck(new_using_deck);
                  setUsingDeck(new_using_deck);
                  setReloading(true);
                }}
              />
            }
          </Grid>
        </>
      );
    }
    return null;
  }

  const renderCardsDrawnByCharacters = () => {
    if (using_deck) {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          <CardsDrawnByCharactersInput
            campaign={campaign}
            campaign_deck={using_deck}
            characters={characters}
            character_drawn_cards={character_drawn_cards}
            cards={cards}
          />
        </Grid>
      );
    }
    return null;
  }

  const renderUsingDeck = () => {
    if (using_deck) {
      const use_filter = {...filter};
      use_filter.deck_ids = using_deck.deck_id;
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox 
              name="Name"
              value={using_deck.name} 
              message={using_deck.name.length > 0 ? "" : "Name Invalid"}
              disabled={disabled || processing}
              onBlur={(changed: string) => {
                const new_using_deck = using_deck;
                new_using_deck.name = changed;
                setUsingDeck(new_using_deck);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectObjectBox
              name="Deck Type"
              data_type='game_deck'
              value={using_deck.deck_id}
              onChange={(changed: string) => {
                const new_using_deck = using_deck;
                new_using_deck.deck_id = changed;
                setUsingDeck(new_using_deck);
                setReloading(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <CenteredMenu
              options={["Controls", "Select Cards in Deck", "Cards Drawn By Characters", "Adventure Spreads"]}
              selected={view_mode}
              onSelect={setViewMode}
            />
          </Grid>
          { view_mode === "Controls" ?
            renderControls()
          : view_mode === "Select Cards in Deck" ?
            renderSelectCardsInDeck()
          : view_mode === "Adventure Spreads" ?
            renderAdventureSpreads()
          : 
            renderCardsDrawnByCharacters()
          }
          <Grid
            size={{
              xs: 6
            }}>
            <ButtonBox
              name={using_deck._id === "" ? "Add" : "Save"}
              disabled={disabled || processing}
              onClick={updateDeck}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <ButtonBox
              name="Back to Deck List"
              disabled={disabled || processing}
              onClick={() => {
                setUsingDeck(null);
              }}
            />
          </Grid>
        </>
      );
    }
    return null;
  };

  const renderAdventureSpreads = () => {
    if (using_deck) {
      return (
        <AdventureSpreadsInput
          campaign={campaign}
          campaign_deck={using_deck}
          cards={cards}
          disabled={disabled}
        />
      );
    }
    return null;
  }

  if (!fetched || loading) {
    return (
      <LoadingBox />
    );
  } else {
    return (
      <Grid container spacing={0} direction="row">
        { renderDecks() }
        { renderUsingDeck() }
      </Grid>
    );
  }
}
